import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { T } from "@/shared/ui/DesignLib";

const PortalLoader = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    const logoRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!gridRef.current || !logoRef.current) return;

        // Create Grid Dots
        const dots = [];
        const rows = 8;
        const cols = 8;
        for (let idx = 0; idx < rows * cols; idx++) {
            dots.push(document.createElement("div"));
        }

        dots.forEach((dot) => {
            dot.style.width = "2px";
            dot.style.height = "2px";
            dot.style.background = T.primary;
            dot.style.borderRadius = "50%";
            dot.style.opacity = "0.1";
            gridRef.current?.appendChild(dot);
        });

        // GSAP Animations
        const tl = gsap.timeline({ repeat: -1 });

        tl.to(dots, {
            duration: 1,
            opacity: 0.6,
            scale: 1.5,
            stagger: {
                grid: [rows, cols],
                from: "center",
                amount: 1.5
            },
            ease: "power2.inOut"
        }).to(dots, {
            duration: 1,
            opacity: 0.1,
            scale: 1,
            stagger: {
                grid: [rows, cols],
                from: "center",
                amount: 1.5
            },
            ease: "power2.inOut"
        });

        gsap.to(logoRef.current, {
            duration: 2,
            opacity: 0.8,
            scale: 1.05,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut"
        });

        return () => {
            tl.kill();
            gsap.killTweensOf(logoRef.current);
        };
    }, []);

    return (
        <div ref={containerRef} style={{
            position: "fixed",
            inset: 0,
            zIndex: 999,
            background: T.bg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 24,
            fontFamily: T.mono
        }}>
            <div style={{ position: "relative", width: 120, height: 120, display: "flex", alignItems: "center", justifyItems: "center" }}>
                <div ref={gridRef} style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    gridTemplateColumns: "repeat(8, 1fr)",
                    gridTemplateRows: "repeat(8, 1fr)",
                    gap: 12,
                    placeItems: "center"
                }} />
                <div ref={logoRef} style={{
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%"
                }}>
                    <div style={{
                        width: 40,
                        height: 40,
                        border: `2px solid ${T.primary}`,
                        borderRadius: "4px",
                        transform: "rotate(45deg)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 0 20px ${T.primary}40`
                    }}>
                        <div style={{
                            width: 12,
                            height: 12,
                            background: T.primary,
                            borderRadius: "1px"
                        }} />
                    </div>
                </div>
            </div>
            
            <div style={{ textAlign: "center" }}>
                <div style={{
                    fontSize: ".65rem",
                    color: T.primary,
                    letterSpacing: ".2em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 8
                }}>
                    Initiating Session
                </div>
                <div style={{
                    fontSize: ".58rem",
                    color: T.dim,
                    maxWidth: 200,
                    lineHeight: 1.5
                }}>
                    Connecting to LinkedHash Oracle network...
                </div>
            </div>

            {/* Scanning line effect */}
            <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "2px",
                background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)`,
                animation: "scan 3s linear infinite"
            }} />

            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes scan {
                    0% { transform: translateY(-100vh); }
                    100% { transform: translateY(100vh); }
                }
            `}} />
        </div>
    );
};

export default PortalLoader;
